import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { FormGuard } from 'src/app/shared/guards/form.guard';

import { AdminUtilitiesComponent } from './admin-utilities/admin-utilities.component';
import { AdminComponent } from './admin.component';
import { ChecklistDashboardComponent } from './checklist-dashboard/checklist-dashboard.component';
import { EditAppConfigPageComponent } from './edit-app-config-page/edit-app-config-page.component';
import { EditBuyerPageComponent } from './edit-buyer-page/edit-buyer-page.component';
import { EditCohortPageComponent } from './edit-cohort-page/edit-cohort-page.component';
import { EditLegalPageComponent } from './edit-legal-page/edit-legal-page.component';
import { EditProgramPageComponent } from './edit-program-page/edit-program-page.component';
import { EditReportConfigPageComponent } from './edit-report-config-page/edit-report-config-page.component';
import { ManageAppConfigPageComponent } from './manage-app-config-page/manage-app-config-page.component';
import { ManageBuyersPageComponent } from './manage-buyers-page/manage-buyers-page.component';
import { ManageCohortsPageComponent } from './manage-cohorts-page/manage-cohorts-page.component';
import { ManageDocusignPageComponent } from './manage-docusign-page/manage-docusign-page.component';
import { ManageLegalPageComponent } from './manage-legal-page/manage-legal-page.component';
import { ManageProgramsPageComponent } from './manage-programs-page/manage-programs-page.component';
import { ManageRegionsPageComponent } from './manage-regions-page/manage-regions-page.component';
import { ManageReportConfigPageComponent } from './manage-report-config-page/manage-report-config-page.component';
import { PaymentDashboardComponent } from './payment-dashboard/payment-dashboard.component';
import { SiteDashboardComponent } from './site-dashboard/site-dashboard.component';
import { AdminSiteDetailPageComponent } from './site-detail-page/admin-site-detail-page.component';
import { StatsDashboardComponent } from './stats-dashboard/stats-dashboard.component';
import { UploadRegionComponent } from './upload-region/upload-region.component';
import { AdminUserPermissionsPageComponent } from './user-permissions-page/admin-user-permissions-page.component';

import { AuthGuard } from '../../core/auth/auth.guard';
import { SharedModule } from '../../shared';
import { UserActivityDashboardComponent } from '../../shared/components/activity-dashboard/user-activity-dashboard.component';
import { DisturbanceReportComponent } from '../../shared/components/disturbance-report/disturbance-report.component';
import { PeriodicReportComponent } from '../../shared/components/periodic-report/periodic-report.component';
import { ProfilePageComponent } from '../../shared/components/profile-page/profile-page.component';
import { ReportResponseComponent } from '../../shared/components/report-response/report-response.component';
import { RoleEnum } from '../../shared/constants/role.enums';
import { MapLayersPageComponent } from '../../shared-map/components/map-layers-pages/map-layers-page.component';
import { LocatePropertyPageComponent } from '../site/locate-property-page/locate-property-page.component';
import { SelectParcelsComponent } from '../site/locate-property-page/select-parcels/select-parcels.component';

const routes: Routes = [
  {
    path: '',
    component: AdminComponent,
    canActivate: [AuthGuard],
    data: {
      roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
    },
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
        component: SiteDashboardComponent,
      },
      {
        path: 'checklist-dashboard',
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
        component: ChecklistDashboardComponent,
      },
      {
        path: 'stats-dashboard',
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
        component: StatsDashboardComponent,
      },
      {
        path: 'payment-dashboard',
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
        component: PaymentDashboardComponent,
      },
      {
        path: 'manage-region',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: ManageRegionsPageComponent,
      },
      {
        path: 'manage-region/upload-region',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.SuperAdmin] },
        component: UploadRegionComponent,
      },
      {
        path: 'manage-region/:id',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: UploadRegionComponent,
      },
      {
        path: 'manage-buyers',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: ManageBuyersPageComponent,
      },
      {
        path: 'edit-buyer/:id',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: EditBuyerPageComponent,
      },
      {
        path: 'manage-report-config',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.SuperAdmin] },
        component: ManageReportConfigPageComponent,
      },
      {
        path: 'manage-legal',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.SuperAdmin] },
        component: ManageLegalPageComponent,
      },
      {
        path: 'manage-docusign',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.SuperAdmin] },
        component: ManageDocusignPageComponent,
      },
      {
        path: 'edit-legal-page/:id',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.SuperAdmin] },
        component: EditLegalPageComponent,
      },
      {
        path: 'edit-report-config/:id',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.SuperAdmin] },
        component: EditReportConfigPageComponent,
      },
      {
        path: 'manage-cohort',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: ManageCohortsPageComponent,
      },
      {
        path: 'edit-cohort',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: EditCohortPageComponent,
      },
      {
        path: 'edit-cohort/:id',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: EditCohortPageComponent,
      },
      {
        path: 'manage-program',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: ManageProgramsPageComponent,
      },
      {
        path: 'edit-program',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: EditProgramPageComponent,
      },
      {
        path: 'edit-program/:id',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: EditProgramPageComponent,
      },
      {
        path: 'site/:id',
        component: AdminSiteDetailPageComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: 'permissions',
        component: AdminUserPermissionsPageComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: 'profile/:id',
        component: ProfilePageComponent,
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: 'user-activity',
        component: UserActivityDashboardComponent,
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: ':id/locate-property',
        component: LocatePropertyPageComponent,
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
        children: [
          {
            path: '',
            redirectTo: 'select-parcels',
            pathMatch: 'full',
          },
          {
            path: 'select-parcels',
            component: SelectParcelsComponent,
          },
        ],
      },
      {
        path: 'site/:id/report-response/:reportId',
        component: ReportResponseComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: 'site/:id/report-response',
        component: ReportResponseComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: 'site/:id/periodic-report/:reportId',
        component: PeriodicReportComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: 'site/:id/disturbance-report/:reportId',
        component: DisturbanceReportComponent,
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: ':id/map-site',
        component: MapLayersPageComponent,
        data: {
          roles: [RoleEnum.Admin, RoleEnum.SuperAdmin, RoleEnum.LimitedAdmin, RoleEnum.FinanceAdmin],
        },
      },
      {
        path: 'manage-app-config',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: ManageAppConfigPageComponent,
      },
      {
        path: 'edit-app-config/:id',
        canActivate: [AuthGuard],
        canDeactivate: [FormGuard],
        data: { roles: [RoleEnum.Admin, RoleEnum.SuperAdmin] },
        component: EditAppConfigPageComponent,
      },
      {
        path: 'admin-utilities',
        canActivate: [AuthGuard],
        data: { roles: [RoleEnum.SuperAdmin] },
        component: AdminUtilitiesComponent,
      },
      {
        path: 'emails',
        canActivate: [AuthGuard],
        data: {
          roles: [RoleEnum.SuperAdmin],
        },
        loadChildren: () => import('./email-admin/email-admin.module').then(m => m.EmailAdminModule),
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes), SharedModule],
  exports: [RouterModule],
})
export class AdminRoutingModule {}
